import { PencilBrush, Point, Shadow, TSimplePathData, Path, PathProps, SerializedPathProps, ObjectEvents, TEvent, TPointerEvent } from "fabric";
import rough from 'roughjs/bin/rough';

declare namespace RoughJS {
  interface RoughGenerator {
    path(d: string, options?: Options): OpSet;
  }

  interface OpSet {
    ops: Op[];
  }

  interface Op {
    op: 'move' | 'lineTo' | 'bcurveTo';
    data: number[];
  }

  interface Options {
    strokeWidth?: number;
    roughness?: number;
    bowing?: number;
    seed?: number;
    stroke?: string;
    fill?: string;
    fillStyle?: string;
    curveFitting?: number;
    simplification?: number;
    dashOffset?: number;
    strokeLineDash?: number[];
    disableMultiStroke?: boolean;
    disableMultiStrokeFill?: boolean;
    preserveVertices?: boolean;
  }
}

interface Point2D {
  x: number;
  y: number;
  pressure?: number;
  timestamp: number;
  velocity?: number;
}

interface StabilizedPoint extends Point2D {
  pressure?: number;
}

class PointStabilizer {
  private points: StabilizedPoint[] = [];
  private readonly baseMaxPoints: number = 7;
  private readonly baseAlpha: number = 0.25;
  private readonly minDistance: number = 3.0;
  private lastVelocity: number = 0.5;
  private lastPressure: number = 0.5;
  private renderPending: boolean = false;
  private lastSmoothedPoint: StabilizedPoint | null = null;
  private rawMode: boolean = false;
  private isFirstPoint: boolean = true;

  setRawMode(enabled: boolean) {
    this.rawMode = enabled;
    this.clear();
  }

  addPoint(point: StabilizedPoint) {
    if (this.rawMode) {
      this.lastPressure = point.pressure ?? 0.5;
      this.lastSmoothedPoint = point;
      return;
    }

    if (this.isFirstPoint) {
      this.initializeWithPoint(point);
      this.isFirstPoint = false;
      return;
    }

    const velocity = this.calculateVelocity(point);
    const pressure = point.pressure ?? this.lastPressure;
    
    const adaptiveMinDistance = this.getAdaptiveMinDistance(velocity, pressure);
    
    if (this.points.length === 0 || this.getDistanceFromLastPoint(point) > adaptiveMinDistance) {
      const smoothedPoint = this.smoothPoint(point, velocity, pressure);
      
      if (this.points.length >= this.getAdaptiveMaxPoints(velocity)) {
        this.points.shift();
      }
      this.points.push(smoothedPoint);

      this.lastVelocity = velocity;
      this.lastPressure = pressure;
      this.lastSmoothedPoint = smoothedPoint;

      if (!this.renderPending) {
        this.renderPending = true;
        requestAnimationFrame(() => {
          this.renderPending = false;
        });
      }
    }
  }

  private initializeWithPoint(point: StabilizedPoint) {
    const maxPoints = this.getAdaptiveMaxPoints(this.lastVelocity);
    for (let i = 0; i < maxPoints; i++) {
      this.points.push({
        x: point.x,
        y: point.y,
        pressure: point.pressure ?? 0.5,
        timestamp: point.timestamp,
        velocity: this.lastVelocity
      });
    }
    this.lastSmoothedPoint = point;
    this.lastPressure = point.pressure ?? 0.5;
  }

  private getAdaptiveAlpha(velocity: number, pressure: number): number {
    if (this.rawMode) {
      return 0.9;
    }
    const velocityFactor = 1 - Math.min(0.9, velocity * 0.8);
    const pressureFactor = 0.5 + (pressure * 0.2);
    return Math.min(0.25, this.baseAlpha * velocityFactor * pressureFactor * 1.2);
  }

  private smoothPoint(point: StabilizedPoint, velocity: number, pressure: number): StabilizedPoint {
    if (!this.lastSmoothedPoint) {
      return point;
    }

    const alpha = this.getAdaptiveAlpha(velocity, pressure);
    
    const x = this.lastSmoothedPoint.x * (1 - alpha) + point.x * alpha;
    const y = this.lastSmoothedPoint.y * (1 - alpha) + point.y * alpha;
    const smoothedPressure = this.lastSmoothedPoint.pressure! * (1 - alpha) + pressure * alpha;

    return {
      x,
      y,
      pressure: smoothedPressure,
      timestamp: point.timestamp,
      velocity
    };
  }

  private getAdaptiveMaxPoints(velocity: number): number {
    if (this.rawMode) {
      return 3;
    }
    return Math.max(16, Math.min(32, Math.round(this.baseMaxPoints * (2.5 - velocity))));
  }

  private getAdaptiveMinDistance(velocity: number, pressure: number): number {
    if (this.rawMode) {
      return this.minDistance * 0.4;
    }
    const velocityFactor = 1 + (velocity * 0.7);
    const pressureFactor = 1.3 - (pressure * 0.3);
    return this.minDistance * velocityFactor * pressureFactor * 2.5;
  }

  private calculateVelocity(point: StabilizedPoint): number {
    if (this.points.length === 0) return this.lastVelocity;
    
    const lastPoint = this.points[this.points.length - 1];
    const timeDiff = point.timestamp - lastPoint.timestamp;
    const distance = this.getDistanceFromLastPoint(point);
    
    const instantVelocity = Math.min(1, distance / (timeDiff || 1) / 1.6);
    return this.lastVelocity * 0.5 + instantVelocity * 0.5;
  }

  private getDistanceFromLastPoint(point: StabilizedPoint): number {
    if (this.points.length === 0) return Infinity;
    const lastPoint = this.points[this.points.length - 1];
    const dx = point.x - lastPoint.x;
    const dy = point.y - lastPoint.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  getCurrentPoint(): StabilizedPoint | null {
    return this.lastSmoothedPoint;
  }

  clear() {
    this.points = [];
    this.lastVelocity = 0.5;
    this.lastPressure = 0.5;
    this.renderPending = false;
    this.lastSmoothedPoint = null;
    this.isFirstPoint = true;
  }
}

class PointBatchProcessor {
  private batchSize: number = 8;
  private batchTimeout: number = 8;
  private currentBatch: StabilizedPoint[] = [];
  private processingTimeout: number | null = null;
  private lastProcessTime: number = 0;

  constructor(private onBatchProcess: (points: StabilizedPoint[]) => void) {}

  addPoint(point: StabilizedPoint) {
    this.currentBatch.push(point);

    if (this.currentBatch.length >= this.batchSize) {
      this.processBatch();
      return;
    }

    if (!this.processingTimeout) {
      const now = performance.now();
      const timeSinceLastProcess = now - this.lastProcessTime;
      const delay = Math.max(0, this.batchTimeout - timeSinceLastProcess);

      this.processingTimeout = window.setTimeout(() => {
        this.processBatch();
      }, delay);
    }
  }

  private processBatch() {
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }

    if (this.currentBatch.length > 0) {
      this.onBatchProcess(this.currentBatch);
      this.currentBatch = [];
      this.lastProcessTime = performance.now();
    }
  }

  clear() {
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }
    this.currentBatch = [];
    this.lastProcessTime = 0;
  }
}

class PathOptimizer {
  private static readonly SPATIAL_HASH_CELL_SIZE = 10;
  private spatialHash: Map<string, Point2D[]> = new Map();

  private getHashKey(x: number, y: number): string {
    const cellX = Math.floor(x / PathOptimizer.SPATIAL_HASH_CELL_SIZE);
    const cellY = Math.floor(y / PathOptimizer.SPATIAL_HASH_CELL_SIZE);
    return `${cellX},${cellY}`;
  }

  optimizePath(points: Point2D[]): Point2D[] {
    if (points.length < 3) return points;

    this.spatialHash.clear();

    points.forEach(point => {
      const key = this.getHashKey(point.x, point.y);
      if (!this.spatialHash.has(key)) {
        this.spatialHash.set(key, []);
      }
      this.spatialHash.get(key)!.push(point);
    });

    return this.reducePoints(points);
  }

  private reducePoints(points: Point2D[]): Point2D[] {
    const result: Point2D[] = [points[0]];
    let lastPoint = points[0];

    for (let i = 1; i < points.length - 1; i++) {
      const point = points[i];
      const nextPoint = points[i + 1];

      const key = this.getHashKey(point.x, point.y);
      const neighbors = this.spatialHash.get(key) || [];
      
      if (this.isSignificantPoint(lastPoint, point, nextPoint, neighbors)) {
        result.push(point);
        lastPoint = point;
      }
    }

    result.push(points[points.length - 1]);
    return result;
  }

  private isSignificantPoint(prev: Point2D, curr: Point2D, next: Point2D, neighbors: Point2D[]): boolean {
    const angle = this.getAngle(prev, curr, next);
    
    if (Math.abs(angle) > 0.1) return true;

    if (Math.abs((curr.pressure || 0.5) - (prev.pressure || 0.5)) > 0.1) return true;

    const hasCloseNeighbors = neighbors.some(n => {
      if (n === curr) return false;
      const dx = n.x - curr.x;
      const dy = n.y - curr.y;
      return (dx * dx + dy * dy) < 25;
    });

    return !hasCloseNeighbors;
  }

  private getAngle(p1: Point2D, p2: Point2D, p3: Point2D): number {
    const dx1 = p2.x - p1.x;
    const dy1 = p2.y - p1.y;
    const dx2 = p3.x - p2.x;
    const dy2 = p3.y - p2.y;
    
    return Math.atan2(dx1 * dy2 - dy1 * dx2, dx1 * dx2 + dy1 * dy2);
  }
}

export class CustomPencilBrush extends PencilBrush {
  private stabilizer: PointStabilizer;
  private batchProcessor: PointBatchProcessor;
  private pathOptimizer: PathOptimizer;
  private readonly minWidth: number = 2.0;
  private readonly maxWidth: number = 3.5;
  private isDrawing: boolean = false;
  private renderRequested: boolean = false;
  private roughGenerator: any;
  private lastPressure: number = 0.5;
  private lastVelocity: number = 0.5;
  private rawMode: boolean = false;

  constructor(canvas: any) {
    super(canvas);
    this.decimate = 1;
    this.stabilizer = new PointStabilizer();
    this.pathOptimizer = new PathOptimizer();
    this.batchProcessor = new PointBatchProcessor(this.processBatch.bind(this));
    this.width = this.minWidth;
    this.roughGenerator = rough.generator();
  }

  setRawMode(enabled: boolean) {
    this.rawMode = enabled;
    this.stabilizer.setRawMode(enabled);
  }

  private processBatch(points: StabilizedPoint[]) {
    if (!this.isDrawing) return;

    const processedPoints = this.rawMode ? points : this.pathOptimizer.optimizePath(points);
    
    if (processedPoints.length > 0) {
      const lastPoint = processedPoints[processedPoints.length - 1];
      
      if (!this.renderRequested) {
        this.renderRequested = true;
        requestAnimationFrame(() => {
          if (this.canvas) {
            this.canvas.requestRenderAll();
          }
          this.renderRequested = false;
        });
      }

      const pressure = lastPoint.pressure ?? 0.5;
      const velocity = this.rawMode ? 0.5 : ((lastPoint as StabilizedPoint).velocity ?? 0.5);
      this.width = this.minWidth + (this.maxWidth - this.minWidth) * pressure * (1 - velocity * 0.5);
    }
  }

  onMouseDown(pointer: Point, options: any) {
    this.isDrawing = true;
    this.stabilizer.clear();
    this.batchProcessor.clear();
    
    const point: StabilizedPoint = {
      x: pointer.x,
      y: pointer.y,
      pressure: (options.e as PointerEvent).pressure ?? 0.5,
      timestamp: performance.now()
    };
    
    this.lastPressure = point.pressure ?? 0.5;

    if (this.rawMode) {
      super.onMouseDown(pointer, options);
    } else {
      this.stabilizer.addPoint(point);
      this.batchProcessor.addPoint(point);
      super.onMouseDown(pointer, options);
    }
  }

  onMouseMove(pointer: Point, options: any) {
    if (!this.canvas || !this.isDrawing) return;

    const currentPoint: StabilizedPoint = {
      x: pointer.x,
      y: pointer.y,
      pressure: (options.e as PointerEvent).pressure ?? 0.5,
      timestamp: performance.now()
    };

    this.lastPressure = currentPoint.pressure ?? 0.5;

    if (this.rawMode) {
      super.onMouseMove(pointer, options);
    } else {
      this.stabilizer.addPoint(currentPoint);
      const stabilizedPoint = this.stabilizer.getCurrentPoint();
      
      if (stabilizedPoint) {
        this.lastVelocity = stabilizedPoint.velocity ?? 0;
        this.batchProcessor.addPoint(stabilizedPoint);
        super.onMouseMove(new Point(stabilizedPoint.x, stabilizedPoint.y), options);
      }
    }
  }

  onMouseUp({ e }: TEvent<TPointerEvent>): boolean {
    this.isDrawing = false;
    this.stabilizer.clear();
    this.batchProcessor.clear();
    return super.onMouseUp({ e });
  }

  private processPoints(points: Point[]): Point2D[] {
    const processedPoints: Point2D[] = points.map(point => ({
      x: point.x,
      y: point.y,
      timestamp: Date.now()
    }));
    
    return movingAverageSmoothing(processedPoints, 3);
  }

  convertPointsToSVGPath(points: Point[]): TSimplePathData {
    const processedPoints = this.processPoints(points);
    const smoothPath = getSmoothPathFromPoints(processedPoints);

    const pathData = this.pathDataToString(smoothPath);
    const roughPath = this.roughGenerator.path(pathData, {
      strokeWidth: this.width,
      roughness: 0.5 + (1 - this.lastPressure) * 0.5,
      bowing: 0.3 + this.lastVelocity * 0.3,
      stroke: this.color as string
    });
    
    if (!roughPath || !roughPath.ops) {
      console.warn('Invalid rough path data, falling back to smooth path');
      return smoothPath;
    }

    return this.roughPathToFabric(roughPath);
  }

  private pathDataToString(pathData: TSimplePathData): string {
    return (pathData as [string, ...number[]][]).map(cmd => {
      const [type, ...points] = cmd;
      return `${type}${points.join(' ')}`;
    }).join(' ');
  }

  private roughPathToFabric(roughPath: any): TSimplePathData {
    const ops = Array.isArray(roughPath.ops) ? roughPath.ops : [];
    const result: [string, ...number[]][] = [];
    
    for (const op of ops) {
      if (!op || !op.op || !Array.isArray(op.data)) continue;

      switch (op.op) {
        case 'move':
          if (op.data.length >= 2) {
            result.push(['M', op.data[0], op.data[1]]);
          }
          break;
        case 'lineTo':
          if (op.data.length >= 2) {
            result.push(['L', op.data[0], op.data[1]]);
          }
          break;
        case 'bcurveTo':
          if (op.data.length >= 6) {
            result.push(['C', 
              op.data[0], op.data[1], 
              op.data[2], op.data[3], 
              op.data[4], op.data[5]
            ]);
          }
          break;
      }
    }
    
    return result.length > 0 ? result as unknown as TSimplePathData : [] as unknown as TSimplePathData;
  }

  createPath(pathData: TSimplePathData): Path<Partial<PathProps>, SerializedPathProps, ObjectEvents> {
    const path = super.createPath(pathData);
    
    if (this.shadow) {
      path.shadow = this.shadow;
    }
    
    path.opacity = 0.90;
    
    return path;
  }
}

function movingAverageSmoothing(points: Point2D[], windowSize: number = 3): Point2D[] {
  if (points.length < windowSize) return points;
  
  const smoothedPoints: Point2D[] = [];
  const halfWindow = Math.floor(windowSize / 2);
  
  smoothedPoints.push({ ...points[0] });
  
  for (let i = 1; i < points.length - 1; i++) {
    const windowStart = Math.max(0, i - halfWindow);
    const windowEnd = Math.min(points.length, windowStart + windowSize);
    const windowPoints = points.slice(windowStart, windowEnd);
    
    let sumX = 0, sumY = 0, sumPressure = 0;
    let alpha = 0.4;
    
    for (let j = 0; j < windowPoints.length; j++) {
      const weight = Math.pow(1 - alpha, windowPoints.length - j - 1);
      sumX += windowPoints[j].x * weight;
      sumY += windowPoints[j].y * weight;
      sumPressure += (windowPoints[j].pressure ?? 0.5) * weight;
    }
    
    const weightSum = (1 - Math.pow(1 - alpha, windowPoints.length)) / alpha;
    
    smoothedPoints.push({
      x: sumX / weightSum,
      y: sumY / weightSum,
      pressure: sumPressure / weightSum,
      timestamp: points[i].timestamp
    });
  }
  
  if (points.length > 1) {
    smoothedPoints.push({ ...points[points.length - 1] });
  }
  
  return smoothedPoints;
}

function getSmoothPathFromPoints(points: Point2D[]): TSimplePathData {
  if (!points || points.length === 0) return [] as unknown as TSimplePathData;
  if (points.length < 2) return [['M', points[0].x, points[0].y]] as unknown as TSimplePathData;

  const pathCommands: [string, ...number[]][] = [['M', points[0].x, points[0].y]];
  
  const getTension = (point: Point2D) => {
    const velocity = (point as StabilizedPoint).velocity ?? 0.5;
    const pressure = point.pressure ?? 0.5;
    return 0.15 + (0.25 * (1 - velocity)) * pressure;
  };
  
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = i === 0 ? points[i] : points[i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = i + 2 < points.length ? points[i + 2] : p2;
    
    const tension = getTension(p1);
    
    const cp1x = p1.x + ((p2.x - p0.x) * tension);
    const cp1y = p1.y + ((p2.y - p0.y) * tension);
    const cp2x = p2.x - ((p3.x - p1.x) * tension);
    const cp2y = p2.y - ((p3.y - p1.y) * tension);

    pathCommands.push(['C', cp1x, cp1y, cp2x, cp2y, p2.x, p2.y]);
  }

  return pathCommands as unknown as TSimplePathData;
}
